import React, { useState, useEffect } from "react";
import { Info, X, FileText, TrendingDown, TrendingUp } from "lucide-react";
import { useFetch } from "../lib/api";
import { SectionLabel, RiskBadge, riskColors, fmtB } from "../components/shared";

const genericCounties = (stateName) => [
  { name: `${stateName} — largest county`, budget: null, fedShare: 15, population: "—", topRisk: "County-level detail rolling out — request early access", riskLevel: "Moderate" }
];

function FederalBars({ title, items, color }) {
  const max = Math.max(...items.map((i) => i.value));
  return (
    <div className="bg-white border border-[#E7E0D2] rounded-2xl p-6">
      <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-4">{title}</p>
      <div className="space-y-3.5">
        {items.map((i) => (
          <div key={i.name}>
            <div className="flex justify-between text-[13px] mb-1">
              <span className="font-medium">{i.name}</span>
              <span className="text-[#1A1914]/55 font-mono-label text-[11px]">{fmtB(i.value)} · {i.pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-[#F3EEE4] overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${(i.value / max) * 100}%`, background: color }} />
            </div>
            {i.risk && <p className="mt-1 text-[11.5px] text-[#C2410C]">⚠ {i.risk}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReportModal({ state, countiesByState, mitigationPlaybook, flowDisclaimer, onClose }) {
  const counties = countiesByState[state.abbr] || genericCounties(state.name);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-7 rp-fade-up" style={{ animationDuration: "0.25s" }} onClick={(e) => e.stopPropagation()} data-testid="report-modal">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono-label text-[10px] text-[#3F4CE0]">AI-Generated Risk & Money Flow Report</p>
            <h3 className="mt-1 font-display font-bold text-[26px]">{state.name}</h3>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#F3EEE4] rp-btn" data-testid="report-close" aria-label="Close"><X size={18} /></button>
        </div>
        <div className="mt-4 p-4 rounded-xl bg-[#FEF3C7] border border-[#FDE68A] text-[12px] leading-relaxed text-[#92400E]" data-testid="report-disclaimer">
          <strong>DISCLAIMER — READ FIRST:</strong> This report is AI-generated using unaudited, curated estimates and is provided strictly for educational purposes. It is not official government data, not financial or legal advice, and must not be relied upon for investment, audit, budgeting or policy decisions. {flowDisclaimer}
        </div>
        <div className="mt-5 grid grid-cols-3 gap-3">
          <div className="bg-[#FAF7F1] rounded-xl p-4 border border-[#E7E0D2]"><p className="font-mono-label text-[9px] text-[#1A1914]/40">State budget</p><p className="font-display font-bold text-[20px]">{fmtB(state.budget)}</p></div>
          <div className="bg-[#FAF7F1] rounded-xl p-4 border border-[#E7E0D2]"><p className="font-mono-label text-[9px] text-[#1A1914]/40">Federal aid</p><p className="font-display font-bold text-[20px]">{fmtB(state.fedAid)}</p></div>
          <div className="bg-[#FAF7F1] rounded-xl p-4 border border-[#E7E0D2]"><p className="font-mono-label text-[9px] text-[#1A1914]/40">GDP (est.)</p><p className="font-display font-bold text-[20px]">{fmtB(state.gdp)}</p></div>
        </div>
        <div className="mt-5">
          <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-2">Highlighted risks</p>
          <ul className="space-y-2">{state.topRisks.map((r) => <li key={r} className="text-[13.5px] flex gap-2"><TrendingDown size={14} className="text-[#E5484D] shrink-0 mt-0.5" />{r}</li>)}</ul>
        </div>
        <div className="mt-5">
          <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-2">Suggested mitigations (AI-drafted)</p>
          <ul className="space-y-2">
            {mitigationPlaybook.filter((m) => m.states.includes(state.abbr) || m.states.includes("All")).slice(0, 3).map((m) => (
              <li key={m.risk} className="text-[13.5px] flex gap-2"><TrendingUp size={14} className="text-[#0E9355] shrink-0 mt-0.5" /><span><strong>{m.risk}:</strong> {m.mitigation}</span></li>
            ))}
          </ul>
        </div>
        <div className="mt-5">
          <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-2">County-level flow</p>
          <div className="space-y-2">
            {counties.map((c) => (
              <div key={c.name} className="flex items-center justify-between text-[13px] bg-[#FAF7F1] border border-[#E7E0D2] rounded-lg px-3.5 py-2.5">
                <span className="font-medium">{c.name}</span>
                <span className="text-[#1A1914]/55">{c.budget ? fmtB(c.budget) : "—"} · fed {c.fedShare}%</span>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-5 text-[11px] text-[#1A1914]/45 italic">Generated by RiskPedia USA · Sam AI · Unaudited · Educational purposes only · Verify against USAspending.gov, CBO and the state ACFR.</p>
      </div>
    </div>
  );
}

export default function MoneyFlow() {
  const { data: mf, loading } = useFetch("/moneyflow");
  const [selected, setSelected] = useState(null);
  const [hover, setHover] = useState(null);
  const [report, setReport] = useState(false);

  const federalSummary = mf?.federalSummary;
  const states = mf?.states || [];
  const countiesByState = mf?.countiesByState || {};
  const mitigationPlaybook = mf?.mitigationPlaybook || [];
  const flowDisclaimer = mf?.disclaimer || "";

  useEffect(() => {
    if (states.length && !selected) setSelected(states.find((s) => s.abbr === "CA") || states[0]);
  }, [states, selected]);

  const counties = selected ? (countiesByState[selected.abbr] || genericCounties(selected.name)) : [];

  if (loading || !federalSummary) return <main className="min-h-[60vh] flex items-center justify-center" data-testid="moneyflow-page"><div className="w-10 h-10 rounded-full border-[3px] border-[#E8590C] border-t-transparent animate-spin" /></main>;

  return (
    <main data-testid="moneyflow-page">
      <section className="bg-[#F3EEE4] border-b border-[#E7E0D2]">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14">
          <SectionLabel color="#E8590C">USA Money Flow · {federalSummary.fiscalYear}</SectionLabel>
          <h1 className="font-display font-bold text-[38px] lg:text-[56px] tracking-tight leading-[1.03] max-w-4xl">Follow <span className="text-[#E8590C]">{fmtB(federalSummary.totalSpending)}</span> from Washington to your county.</h1>
          <p className="mt-4 text-[16px] text-[#1A1914]/65 max-w-2xl">Federal revenue → spending → state transfers → county programs — with risk flags and mitigation playbooks at every level.</p>
          <div className="mt-5 flex items-start gap-2 max-w-3xl p-3.5 rounded-xl bg-white border border-[#E7E0D2]" data-testid="moneyflow-disclaimer">
            <Info size={15} className="text-[#B45309] shrink-0 mt-0.5" />
            <p className="text-[12px] leading-relaxed text-[#1A1914]/60">{flowDisclaimer}</p>
          </div>
        </div>
      </section>

      <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
          {[["Total spending", federalSummary.totalSpending, "#E8590C"], ["Total revenue", federalSummary.totalRevenue, "#0E9355"], ["Deficit", federalSummary.deficit, "#E5484D"], ["Transfers to states", federalSummary.transfersToStates, "#3F4CE0"]].map(([l, v, c]) => (
            <div key={l} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-5">
              <p className="font-mono-label text-[10px] text-[#1A1914]/40">{l}</p>
              <p className="mt-1 font-display font-bold text-[30px]" style={{ color: c }}>{fmtB(v)}</p>
            </div>
          ))}
        </div>
        <div className="grid lg:grid-cols-2 gap-4">
          <FederalBars title="Where the money comes from — federal revenue" items={federalSummary.revenue} color="#0E9355" />
          <FederalBars title="Where it goes — federal spending with risk flags" items={federalSummary.spending} color="#E8590C" />
        </div>
      </section>

      <section className="bg-white border-y border-[#E7E0D2]">
        <div className="max-w-[1320px] mx-auto px-5 lg:px-8 py-14">
          <SectionLabel>Interactive map · click a state</SectionLabel>
          <h2 className="font-display font-bold text-[30px] lg:text-[40px] tracking-tight">State-level flow & <span className="text-[#E5484D]">risk heat.</span></h2>
          <div className="mt-3 flex flex-wrap gap-3 items-center">
            {Object.entries(riskColors).map(([k, v]) => (
              <span key={k} className="inline-flex items-center gap-1.5 text-[12px] text-[#1A1914]/60"><span className="w-2.5 h-2.5 rounded" style={{ background: v }} /> {k}</span>
            ))}
          </div>
          <div className="mt-8 grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 relative">
              <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(12, minmax(0, 1fr))" }} data-testid="usa-tile-map">
                {states.map((s) => (
                  <button key={s.abbr} onClick={() => setSelected(s)} onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(null)}
                    data-testid={`state-tile-${s.abbr}`}
                    className={`aspect-square rounded-lg flex items-center justify-center font-mono-label text-[11px] font-semibold text-white rp-btn ${selected?.abbr === s.abbr ? "ring-2 ring-[#1A1914] ring-offset-2 ring-offset-white" : ""}`}
                    style={{ gridColumn: s.col + 1, gridRow: s.row + 1, background: riskColors[s.riskLevel], opacity: hover && hover.abbr !== s.abbr ? 0.55 : 1, transition: "opacity 0.2s ease, box-shadow 0.2s ease" }}>
                    {s.abbr}
                  </button>
                ))}
              </div>
              {hover && (
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 -translate-y-full bg-[#14130F] text-white text-[12px] px-3.5 py-2 rounded-lg shadow-xl pointer-events-none whitespace-nowrap z-10">
                  <strong>{hover.name}</strong> · budget {fmtB(hover.budget)} · fed aid {fmtB(hover.fedAid)} · {hover.riskLevel}
                </div>
              )}
            </div>
            <div className="lg:col-span-2">
              {selected && (
                <div className="bg-[#FAF7F1] border border-[#E7E0D2] rounded-2xl p-6 rp-fade-up" style={{ animationDuration: "0.3s" }} key={selected.abbr} data-testid="state-detail-panel">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-[26px]">{selected.name}</h3>
                    <RiskBadge level={selected.riskLevel} />
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2.5">
                    <div><p className="font-mono-label text-[9px] text-[#1A1914]/40">Budget</p><p className="font-display font-bold text-[19px]">{fmtB(selected.budget)}</p></div>
                    <div><p className="font-mono-label text-[9px] text-[#1A1914]/40">Federal aid</p><p className="font-display font-bold text-[19px]">{fmtB(selected.fedAid)}</p></div>
                    <div><p className="font-mono-label text-[9px] text-[#1A1914]/40">GDP est.</p><p className="font-display font-bold text-[19px]">{fmtB(selected.gdp)}</p></div>
                  </div>
                  <p className="mt-4 font-mono-label text-[10px] text-[#1A1914]/40">Top risks</p>
                  <ul className="mt-2 space-y-1.5">{selected.topRisks.map((r) => <li key={r} className="text-[13px] flex gap-2"><span className="w-1 h-1 rounded-full bg-[#E5484D] mt-2 shrink-0" />{r}</li>)}</ul>
                  <p className="mt-4 font-mono-label text-[10px] text-[#1A1914]/40">Counties ({counties.length})</p>
                  <div className="mt-2 space-y-1.5 max-h-44 overflow-y-auto pr-1">
                    {counties.map((c) => (
                      <div key={c.name} className="flex items-center justify-between gap-2 text-[12.5px] bg-white border border-[#E7E0D2] rounded-lg px-3 py-2">
                        <span className="font-medium truncate">{c.name}</span>
                        <span className="flex items-center gap-2 shrink-0"><span className="text-[#1A1914]/50">{c.budget ? fmtB(c.budget) : "—"}</span><RiskBadge level={c.riskLevel} /></span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setReport(true)} data-testid="generate-report-btn" className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 text-[14px] font-semibold text-white bg-[#3F4CE0] hover:bg-[#3341C6] rounded-xl rp-btn">
                    <FileText size={15} /> Generate AI risk report
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-[1320px] mx-auto px-5 lg:px-8 py-16">
        <SectionLabel color="#12B76A">Risks → mitigations</SectionLabel>
        <h2 className="font-display font-bold text-[30px] lg:text-[40px] tracking-tight">The national mitigation playbook.</h2>
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mitigation-grid">
          {mitigationPlaybook.map((m) => (
            <div key={m.risk} className="rp-card bg-white border border-[#E7E0D2] rounded-2xl p-6">
              <div className="flex flex-wrap gap-1.5 mb-3">{m.states.map((s) => <span key={s} className="font-mono-label text-[9.5px] px-2 py-0.5 rounded-full bg-[#F3EEE4] border border-[#E7E0D2] text-[#1A1914]/55">{s}</span>)}</div>
              <h3 className="font-display font-semibold text-[19px]">{m.risk}</h3>
              <p className="mt-2 text-[13.5px] leading-relaxed text-[#1A1914]/65">{m.mitigation}</p>
            </div>
          ))}
        </div>
      </section>

      {report && selected && <ReportModal state={selected} countiesByState={countiesByState} mitigationPlaybook={mitigationPlaybook} flowDisclaimer={flowDisclaimer} onClose={() => setReport(false)} />}
    </main>
  );
}
